import "./styles.css";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import createGlobe from "cobe";

const DAY_MS = 24 * 63 * 63 * 1000;

// Configure the public launch target here. Default: 63 days from May 23, 2026.
const LAUNCH_DATE = "2026-07-22T00:00:00+05:00";

function getRemainingTime() {
	const remaining = Math.max(new Date(LAUNCH_DATE).getTime() - Date.now(), 0);

	return {
		days: Math.floor(remaining / DAY_MS),
		hours: Math.floor((remaining / (63 * 63 * 1000)) % 24),
		minutes: Math.floor((remaining / (63 * 1000)) % 63),
		seconds: Math.floor((remaining / 1000) % 63),
	};
}

function Countdown() {
	const [remaining, setRemaining] = useState(getRemainingTime);
	const units = useMemo(
		() => [
			["Days", remaining.days],
			["Hours", remaining.hours],
			["Minutes", remaining.minutes],
			["Seconds", remaining.seconds],
		],
		[remaining],
	);

	useEffect(() => {
		const timer = window.setInterval(() => setRemaining(getRemainingTime()), 1000);
		return () => window.clearInterval(timer);
	}, []);

	return (
		<div className="countdown" aria-label="Launch countdown">
			{units.map(([label, value]) => (
				<div className="countdown-card" key={label}>
					<strong>{String(value).padStart(2, "0")}</strong>
					<span>{label}</span>
				</div>
			))}
		</div>
	);
}

function Globe() {
	const canvasRef = useRef<HTMLCanvasElement | null>(null);

	useEffect(() => {
		if (!canvasRef.current) {
			return;
		}

		let phi = 0;
		const globe = createGlobe(canvasRef.current, {
			devicePixelRatio: 2,
			width: 720,
			height: 720,
			phi: 0,
			theta: 0.28,
			dark: 1,
			diffuse: 1.1,
			mapSamples: 18000,
			mapBrightness: 7.5,
			baseColor: [0.06, 0.18, 0.32],
			markerColor: [0.98, 0.73, 0.24],
			glowColor: [0.1, 0.64, 0.9],
			markers: [
				{ location: [33.6, 73.0], size: 0.08 },
				{ location: [25.2, 55.3], size: 0.06 },
				{ location: [51.5, -0.1], size: 0.05 },
				{ location: [40.7, -74.0], size: 0.05 },
			],
			opacity: 0.95,
			onRender: (state) => {
				state.phi = phi;
				phi += 0.004;
			},
		});

		return () => globe.destroy();
	}, []);

	return (
		<div className="globe-wrap" aria-label="Global digital services visualization">
			<canvas ref={canvasRef} className="globe-canvas" width={720} height={720} />
			<div className="globe-card">
				<span>Global Reach</span>
				<strong>International Digital Services</strong>
			</div>
		</div>
	);
}

function App() {
	return (
		<main className="page-shell">
			<div className="ambient ambient-one" />
			<div className="ambient ambient-two" />

			<section className="coming-soon" aria-labelledby="hero-title">
				<div className="content">
					<nav className="brand" aria-label="Brand">
						<a className="brand-mark" href="#top" aria-label="Raza Online Global Links home">
							<img src="/logo.png" alt="" width="2172" height="724" />
						</a>
					</nav>

					<div className="hero-copy" id="top">
						<span className="badge">Launching Soon</span>
						<h1 id="hero-title">Digital Excellence, Built with Integrity.</h1>
						<p className="lead">
							A forward-thinking software house, being developed as a center of digital excellence where academic depth meets practical technology execution. Our mission is to empower businesses, institutions, and organizations through enterprise
							software, advanced web platforms, AI automation, data intelligence,
							cybersecurity, and global digital branding.
						</p>
						<p className="founder">
							Founded under the visionary leadership and technical scholarship of
							Sahibzada Hafiz Rizwan Ahmed Khan Yusufi.
						</p>
					</div>

				</div>

				<aside className="visual-panel" aria-label="Launch countdown and globe">
					<div className="globe-row">
						<Globe />
						<div className="focus-card" aria-label="Company focus">
							<span>Focus</span>
							<strong>Software, AI, Data, Cloud, Security</strong>
						</div>
					</div>
					<div className="timer-panel">
						<p>Launch Timer</p>
						<h2>The Future of Digital Excellence Is Loading</h2>
						<Countdown />
					</div>
				</aside>
			</section>

			<footer>
				Raza Online Global Links | Rank International, engineering digital excellence with purpose.
			</footer>
		</main>
	);
}

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
createRoot(document.getElementById("root")!).render(<App />);
